import React from 'react'
import { CREDENTIALING_STATUS_LABELS } from '../lib/vocab'

export function KpiCard({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="kpi-card">
      <span className="kpi-label">{label}</span>
      <b className="kpi-value">{value}</b>
      {hint && <span className="kpi-hint">{hint}</span>}
    </div>
  )
}

const RISK_COLORS: Record<string, string> = {
  low: 'badge-green',
  moderate: 'badge-amber',
  high: 'badge-orange',
  critical: 'badge-red',
}

export function RiskBadge({ band }: { band: string }) {
  return <span className={`badge ${RISK_COLORS[band] || 'badge-grey'}`}>{band}</span>
}

// excellent/strong/good/broad — see rc_engine.py's band_match(). "broad" is
// reserved for genuine fallback/expanded-criteria routing, not a score tier,
// so it gets its own distinct color rather than reading as "the worst one."
const MATCH_COLORS: Record<string, string> = {
  excellent: 'badge-green',
  strong: 'badge-blue',
  good: 'badge-amber',
  broad: 'badge-grey',
}

const MATCH_LABELS: Record<string, string> = {
  excellent: 'Excellent Match',
  strong: 'Strong Match',
  good: 'Good Match',
  broad: 'Broad Match',
}

export function MatchBandBadge({ band }: { band: string }) {
  return <span className={`badge ${MATCH_COLORS[band] || 'badge-grey'}`}>{MATCH_LABELS[band] || band}</span>
}

const CREDENTIALING_COLORS: Record<string, string> = {
  unverified: 'badge-grey',
  submitted: 'badge-blue',
  in_review: 'badge-amber',
  approved: 'badge-green',
  action_required: 'badge-orange',
  rejected: 'badge-red',
}

export function CredentialingBadge({ status }: { status: string }) {
  return <span className={`badge ${CREDENTIALING_COLORS[status] || 'badge-grey'}`}>{CREDENTIALING_STATUS_LABELS[status] || status.replace('_', ' ')}</span>
}

export function ProgressBar({ label, percent }: { label: string; percent: number }) {
  const clamped = Math.max(0, Math.min(100, percent))
  return (
    <div className="progress-bar-wrap">
      <div className="progress-bar-head">
        <span className="progress-bar-label">{label}</span>
        <span className="progress-bar-percent">{clamped}%</span>
      </div>
      <div className="progress-bar-track">
        <div className="progress-bar-fill" style={{ width: `${clamped}%` }} />
      </div>
    </div>
  )
}

export function SessionRail({ count }: { count: number }) {
  const capped = Math.min(count, 4)
  return (
    <div className="session-rail">
      {[1, 2, 3, 4].map((n) => (
        <span key={n} className={`session-dot ${n <= capped ? 'filled' : ''} ${n === 3 && capped === 3 ? 'highlight' : ''}`} />
      ))}
      <span className="session-rail-label">{capped} / 4</span>
    </div>
  )
}

export function EmptyState({ icon, children }: { icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="empty">
      {icon}
      <span>{children}</span>
    </div>
  )
}

export function Section({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <div className="section-block">
      <div className="section-head">
        <h2>{title}</h2>
        {note && <span className="sh-note">{note}</span>}
      </div>
      {children}
    </div>
  )
}
